import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { LogIn, Zap, Cloud, Leaf } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { translations } from '../utils/translations';
import { toast } from 'sonner';

const LandingPage = () => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'pt-BR';
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Save language to localStorage when it changes
  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    localStorage.setItem('selectedLanguage', newLang);
  };
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    location: '',
    interest: '',
    why: '',
    linkedin: '',
    privacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const navigate = useNavigate();
  const t = translations[language];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate privacy checkbox
    if (!formData.privacy) {
      toast.error(
        language === 'pt-BR' ? 'Por favor, aceite a política de privacidade' : 
        language === 'en' ? 'Please accept the privacy policy' : 
        'Bitte akzeptieren Sie die Datenschutzerklärung'
      );
      return;
    }

    // Prevent double submission
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    const payload = {
      name: formData.name,
      email: formData.email,
      whatsapp: formData.whatsapp || '',
      location: formData.location,
      interest: formData.interest,
      why: formData.why,
      linkedin: formData.linkedin || '',
      language: language
    };
    
    // Use XMLHttpRequest instead of fetch to avoid body consumed issues
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/leads`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function() {
      setIsSubmitting(false);
      
      if (xhr.status >= 200 && xhr.status < 300) {
        // Show success message in form
        setSubmitSuccess(true);
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            whatsapp: '',
            location: '',
            interest: '',
            why: '',
            linkedin: '',
            privacy: false
          });
          setSubmitSuccess(false);
          setIsDialogOpen(false);
        }, 3000);
      } else {
        try {
          const errorData = JSON.parse(xhr.responseText);
          // Handle different error formats from backend
          let errorMessage = t.form.error;
          
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors
            errorMessage = errorData.detail.map(err => err.msg).join(', ');
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
          
          toast.error(errorMessage);
        } catch (e) {
          toast.error(t.form.error);
        }
      }
    };
    
    xhr.onerror = function() {
      setIsSubmitting(false);
      toast.error(t.form.error);
    };
    
    xhr.send(JSON.stringify(payload));
  };

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-bg-primary/80 backdrop-blur-xl border-b border-border-subtle">
        <div className="container mx-auto px-4 md:px-6">
          {/* Top Row: Language & Login */}
          <div className="flex justify-between items-center py-2 md:py-3">
            <LanguageSwitcher currentLang={language} onLanguageChange={handleLanguageChange} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/login')}
              className="text-text-secondary hover:text-accent-primary"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {t.nav.login}
            </Button>
          </div>
          {/* Bottom Row: Centered Logo */}
          <div className="text-center pb-3 md:pb-4">
            <div className="logo-gradient text-xl md:text-2xl font-black tracking-tight cursor-pointer inline-block" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              smart-signage<span className="text-cyan-400">.com.br</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 px-4 md:px-6 min-h-screen flex items-center">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="display-lg text-text-primary mb-6 animate-fade-in px-4">
            {t.hero.headline}
          </h1>
          <p className="body-lg text-text-secondary mb-8 md:mb-12 max-w-3xl mx-auto px-4">
            {t.hero.subheadline}
          </p>
          <div className="px-4">
            <Button
              size="lg"
              className="btn-primary w-full md:w-auto"
              onClick={() => setIsDialogOpen(true)}
            >
              {t.hero.cta1}
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-bg-secondary/30">
        <div className="container mx-auto max-w-6xl">
          <h2 className="display-md text-center mb-12 md:mb-16 px-4">{t.about.title}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="feature-card">
              <div className="flex justify-center mb-6">
                <Zap className="icon-pulse w-16 h-16 md:w-20 md:h-20 text-cyan-400" />
              </div>
              <h3 className="h3 mb-4 text-accent-primary text-center">{t.about.card1Title}</h3>
              <p className="body-md text-text-secondary text-center">{t.about.card1Desc}</p>
            </Card>
            <Card className="feature-card">
              <div className="flex justify-center mb-6">
                <Cloud className="icon-pulse w-16 h-16 md:w-20 md:h-20 text-cyan-400" />
              </div>
              <h3 className="h3 mb-4 text-accent-primary text-center">{t.about.card2Title}</h3>
              <p className="body-md text-text-secondary text-center">{t.about.card2Desc}</p>
            </Card>
            <Card className="feature-card">
              <div className="flex justify-center mb-6">
                <Leaf className="icon-pulse w-16 h-16 md:w-20 md:h-20 text-cyan-400" />
              </div>
              <h3 className="h3 mb-4 text-accent-primary text-center">{t.about.card3Title}</h3>
              <p className="body-md text-text-secondary text-center">{t.about.card3Desc}</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Interest Form Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-bg-secondary border-border-primary">
          <DialogHeader>
            <DialogTitle className="h2 text-text-primary">{t.form.title}</DialogTitle>
            <DialogDescription className="body-md text-text-secondary">
              {t.form.subtitle}
            </DialogDescription>
          </DialogHeader>
          
          {submitSuccess ? (
            <div className="py-12 text-center">
              <div className="w-20 h-20 bg-accent-primary rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-bg-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="h2 text-accent-primary mb-4">{language === 'pt-BR' ? 'Sucesso!' : language === 'en' ? 'Success!' : 'Erfolg!'}</h3>
              <p className="body-lg text-text-primary">{t.form.success}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block body-sm font-medium mb-2 text-text-primary">{t.form.name} *</label>
              <Input
                required
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t.form.namePlaceholder}
                className="input-field"
              />
            </div>

            <div>
              <label className="block body-sm font-medium mb-2 text-text-primary">{t.form.email} *</label>
              <Input
                required
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t.form.emailPlaceholder}
                className="input-field"
              />
            </div>

            <div>
              <label className="block body-sm font-medium mb-2 text-text-primary">{t.form.whatsapp}</label>
              <Input
                value={formData.whatsapp}
                onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                placeholder={t.form.whatsappPlaceholder}
                className="input-field"
              />
            </div>

            <div>
              <label className="block body-sm font-medium mb-2 text-text-primary">{t.form.location} *</label>
              <Input
                required
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder={t.form.locationPlaceholder}
                className="input-field"
              />
            </div>

            <div>
              <label className="block body-sm font-medium mb-2 text-text-primary">{t.form.interest} *</label>
              <Select required value={formData.interest} onValueChange={(value) => handleInputChange('interest', value)}>
                <SelectTrigger className="input-field">
                  <SelectValue placeholder="..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t.form.interestOptions.admin}</SelectItem>
                  <SelectItem value="sales">{t.form.interestOptions.sales}</SelectItem>
                  <SelectItem value="partner">{t.form.interestOptions.partner}</SelectItem>
                  <SelectItem value="customer">{t.form.interestOptions.customer}</SelectItem>
                  <SelectItem value="investor">{t.form.interestOptions.investor}</SelectItem>
                  <SelectItem value="other">{t.form.interestOptions.other}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block body-sm font-medium mb-2 text-text-primary">{t.form.why} *</label>
              <Textarea
                required
                value={formData.why}
                onChange={(e) => handleInputChange('why', e.target.value)}
                placeholder={t.form.whyPlaceholder}
                className="input-field min-h-24"
              />
            </div>

            <div>
              <label className="block body-sm font-medium mb-2 text-text-primary">{t.form.linkedin}</label>
              <Input
                type="url"
                value={formData.linkedin}
                onChange={(e) => handleInputChange('linkedin', e.target.value)}
                placeholder={t.form.linkedinPlaceholder}
                className="input-field"
              />
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="privacy"
                checked={formData.privacy}
                onCheckedChange={(checked) => handleInputChange('privacy', checked)}
                className="mt-1"
              />
              <label htmlFor="privacy" className="body-sm text-text-secondary cursor-pointer">
                {t.form.privacy} *
              </label>
            </div>

            <Button
              type="submit"
              size="lg"
              className="btn-primary w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? '...' : t.form.submit}
            </Button>
          </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;